import { Modal, TextInput, Textarea, Button } from "@mantine/core";
import { useForm } from "@mantine/form";
import React, { useState } from "react";
import { Endpoints } from "../../lib/endpoints";
import { showNotification } from "@mantine/notifications";

interface CreateSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateSignatureModal: React.FC<CreateSignatureModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      issuedTo: "",
      description: "",
    },
    validate: {
      issuedTo: (value) => (value.length < 2 ? "Issued To is required" : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("t");
      const response = await fetch(Endpoints.CreateSignature().route, {
        method: Endpoints.CreateSignature().method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (response.ok) {
        showNotification({
          title: "Success",
          message: "Signature generated successfully",
          color: "green",
        });
        form.reset();
        onSuccess();
      } else {
        showNotification({
          title: "Error",
          message: data.message || "Failed to generate signature",
          color: "red",
        });
      }
    } catch (error) {
      showNotification({
        title: "Error",
        message: "An unexpected error occurred",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Generate New Signature"
      styles={{
        header: { backgroundColor: "#1f2937", color: "white" },
        body: { backgroundColor: "#1f2937", color: "white" },
        title: { fontSize: "1.25rem", fontWeight: 600 },
      }}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)} className="space-y-4">
        <TextInput
          label="Issued To"
          placeholder="e.g. A bank, Nigerian Government, An employee"
          required
          {...form.getInputProps("issuedTo")}
          styles={{
            label: { color: "#e5e7eb", marginBottom: "0.25rem" },
            input: {
              backgroundColor: "#111827",
              color: "white",
              borderColor: "#374151",
            },
          }}
        />

        <Textarea
          label="Description (Optional)"
          placeholder="Additional information about this signature..."
          minRows={3}
          {...form.getInputProps("description")}
          styles={{
            label: { color: "#e5e7eb", marginBottom: "0.25rem" },
            input: {
              backgroundColor: "#111827",
              color: "white",
              borderColor: "#374151",
            },
          }}
        />

        <div className="flex justify-end pt-4 space-x-3">
          <Button
            variant="default"
            onClick={onClose}
            className="bg-gray-700 text-white hover:bg-gray-600 border-none"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            className="bg-purple-600 hover:bg-purple-700 transition-colors"
          >
            Generate Signature
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSignatureModal;
